/**
 * IA Corner page component.
 * Main page component for displaying AI-generated corner content.
 * Fetches and displays both content (reels) and promos (highlights) for an IA Corner,
 * with pagination support for both horizontal and vertical navigation.
 */

import Direction from '@shared/types/enums/direction';
import Main from '@shared/types/enums/main';
import TMain from '../templates/main';
import useIACornerContent from '@shared/components/utils/hooks/useIACornerContent';
import useIACornerPromos from '@shared/components/utils/hooks/useIACornerPromos';
import { routerSelector } from '@shared/store/slices/router.slice';
import { useAppSelector } from '@shared/store/hooks';

/**
 * IA Corner page component.
 * Renders the main template with IA Corner content (reels) and promos (highlights),
 * handling pagination for both content types.
 *
 * @returns {JSX.Element} IA Corner page component
 */
export default function IACorner() {
  const {
    params: { current: currentParams },
  } = useAppSelector(routerSelector);

  const {
    complete: contentComplete,
    reels,
    onPagination: onContentPagination,
  } = useIACornerContent({
    id: currentParams?.id as string,
  });

  const {
    complete: promosComplete,
    highlights,
    onPagination: onPromosPagination,
  } = useIACornerPromos({
    id: currentParams?.id as string,
  });

  return (
    <TMain
      complete={contentComplete && promosComplete}
      hideBackground={true}
      rails={reels}
      sliders={highlights}
      startsInMenu={false}
      startsInSlider={true}
      onPagination={(type: Main, direction: Direction) => {
        if (type === Main.RAILS) {
          onContentPagination(type, direction);
        } else if (type === Main.SLIDERS) {
          onPromosPagination();
        }
      }}
    />
  );
}
